import { useVirtualizer } from '@tanstack/react-virtual'
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactElement } from 'react'
import { cn } from '@/common/lib/cn/cn'
import './virtualCardGrid.css'

export type VirtualCardGridProps = {
    itemCount: number
    rowHeight: number
    minItemWidth: number
    gap?: number
    rowGap?: number
    renderItem: (index: number) => ReactElement
    height?: number
    overscan?: number
    rounded?: boolean
    className?: string
    onVisibleIndicesChange?: (indices: number[], scrollIndex: number) => void
    scrollToIndex?: number
}

const getColumnCount = (containerWidth: number, minItemWidth: number, gap: number) =>
    Math.max(1, Math.floor((containerWidth + gap) / (minItemWidth + gap)))

export const VirtualCardGrid = ({
    itemCount,
    rowHeight,
    minItemWidth,
    gap = 12,
    rowGap,
    renderItem,
    height = 400,
    overscan = 5,
    rounded = true,
    className,
    onVisibleIndicesChange,
    scrollToIndex,
}: VirtualCardGridProps) => {
    const parentRef = useRef<HTMLDivElement>(null)
    const onVisibleIndicesChangeRef = useRef(onVisibleIndicesChange)
    onVisibleIndicesChangeRef.current = onVisibleIndicesChange
    const hasRestoredScroll = useRef(false)
    const [containerWidth, setContainerWidth] = useState(0)

    useLayoutEffect(() => {
        const element = parentRef.current
        if (!element) return

        const observer = new ResizeObserver(([entry]) => {
            if (!entry) return
            setContainerWidth(entry.contentRect.width)
        })

        observer.observe(element)
        setContainerWidth(element.offsetWidth)

        return () => observer.disconnect()
    }, [])

    const columnGap = gap
    const effectiveRowGap = rowGap ?? gap
    const virtualRowSize = rowHeight + effectiveRowGap

    const columnCount = useMemo(
        () => (containerWidth > 0 ? getColumnCount(containerWidth, minItemWidth, columnGap) : 1),
        [containerWidth, minItemWidth, columnGap]
    )

    const rowCount = itemCount > 0 ? Math.ceil(itemCount / columnCount) : 0

    const virtualizer = useVirtualizer({
        count: rowCount,
        getScrollElement: () => parentRef.current,
        estimateSize: () => virtualRowSize,
        overscan,
    })

    useEffect(() => {
        if (scrollToIndex == null || scrollToIndex < 0 || itemCount === 0 || rowCount === 0 || !parentRef.current)
            return

        const targetRow = Math.floor(scrollToIndex / columnCount)

        if (scrollToIndex === 0) {
            hasRestoredScroll.current = false
            virtualizer.scrollToIndex(0, { align: 'start' })
            return
        }

        if (hasRestoredScroll.current) return
        hasRestoredScroll.current = true
        virtualizer.scrollToIndex(Math.min(targetRow, rowCount - 1), { align: 'start' })
    }, [scrollToIndex, itemCount, rowCount, columnCount, virtualizer])

    const virtualItems = virtualizer.getVirtualItems()
    const totalSize = virtualizer.getTotalSize()
    const visibleRowsKey = virtualItems.map(v => v.index).join(',')

    useEffect(() => {
        if (!onVisibleIndicesChangeRef.current) return

        const rowIndices = visibleRowsKey ? visibleRowsKey.split(',').map(Number) : []
        const indices: number[] = []

        for (const rowIndex of rowIndices) {
            const startIndex = rowIndex * columnCount
            const endIndex = Math.min(startIndex + columnCount, itemCount)
            for (let index = startIndex; index < endIndex; index += 1) {
                indices.push(index)
            }
        }

        const scrollIndex = rowIndices.length > 0 ? rowIndices[0] * columnCount : 0
        onVisibleIndicesChangeRef.current(indices, scrollIndex)
    }, [visibleRowsKey, columnCount, itemCount])

    const gridStyle = {
        ['--virtual-card-grid-gap']: `${columnGap}px`,
        ['--virtual-card-grid-row-gap']: `${effectiveRowGap}px`,
        ['--virtual-card-grid-columns']: String(columnCount),
        ['--virtual-card-grid-row-height']: `${rowHeight}px`,
    } as CSSProperties

    return (
        <div
            ref={parentRef}
            className={cn(
                'virtual-card-grid-scroll overflow-y-auto w-full',
                { 'virtual-card-grid-scroll-rounded': rounded },
                className
            )}
            style={{ height: `${height}px`, ...gridStyle }}
        >
            <div className='virtual-card-grid-inner' style={{ height: `${totalSize}px`, position: 'relative' }}>
                {virtualItems.map(virtualRow => {
                    const startIndex = virtualRow.index * columnCount
                    const endIndex = Math.min(startIndex + columnCount, itemCount)

                    return (
                        <div
                            key={virtualRow.key}
                            className='virtual-card-grid-row'
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: `${rowHeight}px`,
                                transform: `translateY(${virtualRow.start}px)`,
                            }}
                        >
                            {Array.from({ length: endIndex - startIndex }, (_, offset) => {
                                const itemIndex = startIndex + offset
                                const item = renderItem(itemIndex)
                                return (
                                    <div key={item.key ?? itemIndex} className='virtual-card-grid-item'>
                                        {item}
                                    </div>
                                )
                            })}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}
